import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Badge } from 'primereact/badge';
import { useTranslation } from 'localization';

/** Decodes HTML entities (e.g. &#39; → ') stored in notification text fields. */
const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};
import { useUserIdentityContext } from '../../../context/UserIdentityContext';
import { useApiClients } from '../../../context/ApiClientsContext';
import { notificationPollingService } from '../../../services/NotificationPollingService';
import { NOTIFICATION_TYPE_CONFIGS, NotificationTypeConfig } from '../../../config/notificationTypes';
import './NotificationBell.scss';

interface NotificationBellProps {
  className?: string;
  onNotificationClick?: () => void;
  onMenuStateChange?: (isOpen: boolean) => void;
  onViewAllClick?: () => void;
  isActive?: boolean; // Whether the notification icon should show active/filled state
}

interface Notification {
  id: string;
  user_id: string;
  sphere_id?: string;
  center_id?: string;
  type: string;
  title: string;
  content: string;
  summary?: string;
  metadata?: any;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  created_at: string;
  read_at?: string;
}

interface NotificationFilter {
  value: string | null;
  label: string;
  icon?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '', onNotificationClick, onMenuStateChange, onViewAllClick, isActive = false }) => {
  const { t } = useTranslation();
  // Generate a stable unique ID for this component instance
  const componentId = useMemo(() => `notification-bell-${Math.random().toString(36).slice(2, 11)}`, []);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  const { identityId: currentUserIdentityId } = useUserIdentityContext();

  // Translate notification type configurations at runtime
  const NOTIFICATION_TYPES: NotificationTypeConfig[] = useMemo(
    () =>
      NOTIFICATION_TYPE_CONFIGS.map((config) => ({
        value: config.value,
        label: t(config.labelKey),
        icon: config.icon,
      })),
    [t]
  );

  // Generate filter options dynamically from notification types
  const FILTER_OPTIONS: NotificationFilter[] = [
    { value: null, label: t('notifications.filters.all'), icon: 'pi-list' },
    ...NOTIFICATION_TYPES.map((type) => ({
      value: type.value,
      label: type.label,
      icon: type.icon,
    })),
  ];

  // Use safe hook to avoid throwing errors
  let notificationClient = null;
  try {
    const clients = useApiClients();
    notificationClient = clients?.notificationClient;
  } catch (error) {
    // API clients not ready yet - this is expected during initialization
    console.debug('NotificationBell: API clients not ready yet', error);
  }

  // Fetch recent notifications
  const fetchRecentNotifications = async () => {
    if (!currentUserIdentityId || !notificationClient) return;

    setIsLoading(true);
    try {
      const response = await notificationClient.get(`/api/notifications/${currentUserIdentityId}/unread`, {
        params: {
          limit: 10,
          offset: 0,
        },
      });

      let fetchedNotifications: Notification[] = [];
      if (response.data?.success) {
        fetchedNotifications = response.data.data;
      } else if (Array.isArray(response.data)) {
        fetchedNotifications = response.data;
      } else {
        fetchedNotifications = [];
      }

      setAllNotifications(fetchedNotifications);
      applyFilter(fetchedNotifications, selectedFilter);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply notification filter
  const applyFilter = (notificationList: Notification[], filterType: string | null) => {
    if (filterType) {
      const filtered = notificationList.filter((notification) => notification.type === filterType);
      setNotifications(filtered);
    } else {
      setNotifications(notificationList);
    }
  };

  // Handle filter change
  const handleFilterChange = (filterValue: string | null) => {
    setSelectedFilter(filterValue);
    applyFilter(allNotifications, filterValue);
  };

  // Get notification type configuration
  const getNotificationTypeConfig = (type: string): NotificationTypeConfig | undefined => {
    return NOTIFICATION_TYPES.find((config) => config.value === type);
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!currentUserIdentityId || !notificationClient) return;

    try {
      await notificationClient.patch(`/api/notifications/${notificationId}/read`, {
        userId: currentUserIdentityId,
      });

      // Update both notification lists
      const updateNotification = (n: Notification) => (n.id === notificationId ? { ...n, status: 'read' as const } : n);

      setAllNotifications((prev) => prev.map(updateNotification));
      setNotifications((prev) => prev.map(updateNotification));

      // Refresh stats immediately
      notificationPollingService.refresh();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read (bulk operation)
  const markAllAsRead = async () => {
    if (isMarkingAllRead || !currentUserIdentityId || !notificationClient) return;

    setIsMarkingAllRead(true);
    try {
      // Single bulk API call to mark ALL notifications as read
      await notificationClient.patch(
        `/api/notifications/${currentUserIdentityId}/mark-all-read`,
        {} // No filters - mark ALL as read
      );

      // Clear notifications from UI immediately
      setNotifications([]);
      setAllNotifications([]);
      setUnreadCount(0);

      // Refresh notification polling to sync with server
      notificationPollingService.refresh();

      // Fetch fresh notifications to update the list
      await fetchRecentNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  // Subscribe to notification polling service (singleton prevents duplicate polling)
  useEffect(() => {
    // Only subscribe if we have the required dependencies
    if (!currentUserIdentityId || !notificationClient) {
      return;
    }

    // Subscribe to the singleton polling service
    notificationPollingService.subscribe(
      componentId,
      currentUserIdentityId,
      notificationClient,
      (_stats, unreadCount) => {
        // Update unread count when stats are received
        setUnreadCount(unreadCount);
      },
      (error) => {
        console.error('Notification polling error:', error);
      }
    );

    // Unsubscribe on unmount
    return () => {
      notificationPollingService.unsubscribe(componentId);
    };
  }, [currentUserIdentityId, notificationClient, componentId]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      fetchRecentNotifications();
    }
  }, [isDropdownOpen]);

  // Notify parent when menu state changes
  useEffect(() => {
    if (onMenuStateChange) {
      onMenuStateChange(isDropdownOpen);
    }
  }, [isDropdownOpen, onMenuStateChange]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    if (isDropdownOpen || !bellRef.current) {
      setIsDropdownOpen(!isDropdownOpen);
      return;
    }

    // Calculate position relative to the bell when opening
    const bellRect = bellRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    setDropdownPosition({
      top: bellRect.bottom + 10,
      right: viewportWidth - bellRect.right,
    });

    setIsDropdownOpen(true);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t('notifications.time.justNow');
    if (diffMins < 60) return t('notifications.time.minutesAgo', { count: diffMins });

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('notifications.time.hoursAgo', { count: diffHours });

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return t('notifications.time.daysAgo', { count: diffDays });

    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (notification.status !== 'read') {
      markAsRead(notification.id);
    }

    // Call parent handler if provided
    if (onNotificationClick) {
      onNotificationClick();
    }

    // Close dropdown
    setIsDropdownOpen(false);
  };

  const renderNotificationContent = () => {
    if (isLoading) {
      return (
        <div className="notification-loading">
          <i className="pi pi-spin pi-spinner"></i>
          <span>{t('notifications.loading')}</span>
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className="notification-empty">
          <i className="pi pi-inbox"></i>
          <p>{t('notifications.empty')}</p>
        </div>
      );
    }

    return (
      <div className="notification-list">
        {notifications.map((notification) => {
          const typeConfig = getNotificationTypeConfig(notification.type);
          return (
            <button
              key={notification.id}
              className={`notification-item ${notification.status === 'read' ? 'read' : 'unread'}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-item-header">
                <div className="notification-title-section">
                  {typeConfig && <i className={`pi ${typeConfig.icon} notification-type-icon`}></i>}
                  <span className="notification-title">{notification.title}</span>
                </div>
                <span className="notification-time">{formatTime(notification.created_at)}</span>
              </div>
              <div className="notification-content">{decodeHtmlEntities(notification.content)}</div>
              {notification.status !== 'read' && <div className="notification-unread-indicator"></div>}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`notification-bell-wrapper ${className}`}>
      <button
        ref={bellRef}
        className={`notification-bell notification-bell--atlas ${isDropdownOpen ? 'is-active' : ''} ${unreadCount > 0 ? 'has-unread' : ''} ${className}`}
        onClick={toggleDropdown}
        aria-label={unreadCount > 0 ? t('notifications.ariaLabel.withUnread', { count: unreadCount }) : t('notifications.ariaLabel.default')}
        aria-expanded={isDropdownOpen}
      >
        <i className="pi pi-bell mobile-hide" aria-hidden="true"></i>
        <i className={`${isActive ? 'Notification-icon-filled' : 'Notification-icon'} mobile-show`} aria-hidden="true"></i>
        {unreadCount > 0 && <Badge value={unreadCount > 99 ? '99+' : unreadCount.toString()} severity="danger" className="notification-badge" />}
      </button>

      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="notification-dropdown"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 9999,
          }}
        >
          <div className="notification-dropdown-header">
            <h3>{t('notifications.header')}</h3>
            {unreadCount > 0 && (
              <button className="mark-all-read-btn" onClick={markAllAsRead} disabled={isMarkingAllRead}>
                {isMarkingAllRead ? (
                  <>
                    <i className="pi pi-spin pi-spinner spinner-icon"></i>
                    {t('notifications.marking')}
                  </>
                ) : (
                  t('notifications.markAllAsRead')
                )}
              </button>
            )}
          </div>

          <div className="notification-filter-section">
            <div className="filter-label">{t('notifications.filterLabel')}</div>
            <div className="filter-buttons">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value || 'all'}
                  type="button"
                  className={`filter-button ${selectedFilter === option.value ? 'active' : ''}`}
                  onClick={() => handleFilterChange(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleFilterChange(option.value);
                    }
                  }}
                  aria-pressed={selectedFilter === option.value}
                  aria-label={t('notifications.filterAriaLabel', { type: option.label })}
                >
                  {option.icon && <i className={`pi ${option.icon}`}></i>}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="notification-dropdown-content">{renderNotificationContent()}</div>

          <div className="notification-dropdown-footer">
            <button
              className="view-all-link"
              onClick={() => {
                setIsDropdownOpen(false);
                if (onViewAllClick) {
                  onViewAllClick();
                }
              }}
            >
              {t('notifications.viewAll')}
              <i className="pi pi-arrow-right view-all-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
